import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  Lexicon,
  QueryFilter,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import {
  TermField,
  TermPickerOption,
} from '../term-picker/term-picker.component';
import {
  QbGroup,
  QbJoiner,
  QbModel,
  QbNodeAction,
  QbNodeView,
  QbArtifactRef,
  QbTerm,
  addChildGroup,
  addExclusion,
  addGroundedGroup,
  addArtifact,
  artifactKind,
  addGroup,
  addTerm,
  canMerge,
  canSetJoiner,
  canSplit,
  deriveBuilderModel,
  deriveNodeTree,
  describeBlockers,
  hoistInertExclusions,
  inertExclusions,
  isFreeText,
  isIncomplete,
  mergeGroups,
  mergeUnavailable,
  pathKey,
  plural,
  removeGroup,
  removeArtifact,
  removeNode,
  removeTerm,
  setGroupJoiner,
  setNodeOperator,
  setTopJoiner,
  renameTerm,
  serializeQuery,
  splitGroup,
  splitUnavailable,
  topJoinerUnavailable,
} from './query-builder.model';

/** Terms a position-named group shows in its title before trailing off. */
const TITLE_TERMS = 2;

/** The three readings of one query. */
export type QbView = 'zones' | 'structure' | 'text';

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * What kind of thing an entity filter points at. Two formats reach here: the
 * picker writes `type:id` (`city:berlin_4182096`), and the query generator
 * writes corpus keys as `pne:type.id` (`pne:company.assemblin`) — reading the
 * first segment of that one gave every generated entity pill the badge "Pne".
 */
function entityKind(value: string): string {
  const raw = (value ?? '').trim();
  const body = raw.startsWith('pne:') ? raw.slice(4) : raw;
  const kind = (body.split(/[:.]/)[0] ?? '').trim();
  return kind ? capitalise(kind) : 'Entity';
}

/** Reads as a sentence: `“Leclerc” and “leclerc”`. */
function sentence(terms: string[]): string {
  const quoted = terms.map((t) => `“${t}”`);
  if (quoted.length < 2) return quoted[0] ?? '';
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
}

// The query editor both hosts use. It renders the zones the model derives and
// emits a new tree for every edit — it holds no query state of its own, so the
// host stays the single owner of the tree.
//
@Component({
  selector: 'app-query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss'],
})
export class QueryBuilderComponent implements OnChanges {
  @Input() tree: YukkaQuery | null = null;
  @Input() lexicon: Lexicon | null = null;
  @Input() editing = false;
  /**
   * `+ group` adds a second required group, which turns entity identification
   * into topic filtering. Collection queries want it; the DD page does not.
   */
  @Input() allowGroups = false;
  /** Titles read as the role a group plays (DD) or as its position. */
  @Input() naming: 'role' | 'position' = 'position';
  @Input() fields: TermField[] = ['text'];
  /**
   * Saved queries and portfolios this host lets the user embed, behind a picker
   * each. Term pickers never see them: an embedded artifact is a block of its
   * own, not a term inside a bag.
   *
   * Split by kind as it arrives, so the two lists cannot go stale and no getter
   * hands the picker a new array on every change-detection pass.
   */
  @Input() set artifacts(list: TermPickerOption[]) {
    this.savedQueries = (list ?? []).filter(
      (a) => artifactKind(a.field) === 'query',
    );
    this.portfolios = (list ?? []).filter(
      (a) => artifactKind(a.field) === 'portfolio',
    );
  }
  /** The picker panel opens in a CDK overlay, so the host has to name it. */
  @Input() panelClass = '';
  /** Terms shown before the rest collapse behind “+ N more”. */
  @Input() collapseAfter = 12;
  @Input() asText = false;

  @Output() treeChange = new EventEmitter<YukkaQuery>();

  model: QbModel = deriveBuilderModel(null);
  /**
   * Everything below is a pure function of `tree` that the template reads more
   * than once — the read-only banner alone read the three exclusion values
   * fourteen times per change-detection pass. Derived here, once per tree.
   */
  queryText = '';
  blockerSummary = '';
  /**
   * The terms that sit in an OR bag negated, and so exclude nothing. When these
   * are the only thing stopping the zones, the banner names them and offers to
   * put them where they work instead of reporting a shape the user can't act on.
   */
  inertExclusions: string[] = [];
  onlyInertExclusions = false;
  inertExclusionList = '';
  /**
   * The tree mirrored 1:1. What the zones cannot draw is shown here instead of
   * being reshaped into something that matches different articles.
   */
  nodeTree: QbNodeView = deriveNodeTree(null);
  /** Which reading of the query is on screen. */
  view: QbView = 'zones';
  pickerAt: string | null = null;
  /** The two artifact lists, each behind its own picker. See `artifacts`. */
  savedQueries: TermPickerOption[] = [];
  portfolios: TermPickerOption[] = [];

  private expanded = new Set<string>();
  /** Set once the user chooses a view, so an edit does not pull them out of it. */
  private viewPick: QbView | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // The host asking for text overrides a view the user picked earlier.
    if (changes.asText) this.viewPick = null;
    if (!changes.tree && !changes.lexicon && !changes.asText) return;
    this.model = deriveBuilderModel(this.tree, this.lexicon);
    this.nodeTree = deriveNodeTree(this.tree, this.lexicon);
    this.queryText = serializeQuery(this.tree);
    this.blockerSummary = describeBlockers(this.model.blockers);
    this.inertExclusions = inertExclusions(this.model);
    this.onlyInertExclusions =
      !!this.model.blockers.length &&
      this.model.blockers.every((b) => b.kind === 'inert-exclusion');
    this.inertExclusionList = sentence(this.inertExclusions);
    this.resolveView();
  }

  // --- Which reading is on screen -------------------------------------------

  /**
   * Zones when they can show the query faithfully, the structure otherwise. A
   * shape the zones refuse is not read-only any more: it is drawn as it is, so
   * the query never has to be converted into one that means something else.
   */
  private resolveView(): void {
    const fallback: QbView = this.asText
      ? 'text'
      : this.model.representable
        ? 'zones'
        : 'structure';
    // A pick the query has outgrown gives way: once an edit makes the zones able
    // to show it, "zones" is what a user who never chose expects to see.
    this.view =
      this.viewPick === 'zones' && !this.model.representable
        ? fallback
        : (this.viewPick ?? fallback);
  }

  setView(view: QbView): void {
    this.viewPick = view;
    this.resolveView();
  }

  /** Disabled with a reason rather than hidden — the choice stays discoverable. */
  get zonesReason(): string {
    return this.model.representable
      ? ''
      : `This query has ${this.blockerReason}`;
  }

  // --- Reading the model ----------------------------------------------------

  get joinerLabel(): string {
    return this.model.joiner === 'and' ? 'and also mentions' : 'or instead';
  }

  /**
   * Whether the joiner between blocks is a control. Topic queries only: on a
   * company query, "every" over name forms would ask for all of them at once.
   */
  get canSetTopJoiner(): boolean {
    return (
      this.editing && this.allowGroups && !topJoinerUnavailable(this.model)
    );
  }

  /** The control is shown as soon as a host edits topic queries — disabled
   *  rather than hidden, so the choice is discoverable either way. */
  get showTopJoiner(): boolean {
    return this.editing && this.allowGroups;
  }

  get topJoinerHint(): string {
    const blocked = topJoinerUnavailable(this.model);
    return blocked
      ? `Cannot change while ${blocked}`
      : 'How the blocks below combine. Exclusions keep working either way.';
  }

  setTopJoinerTo(joiner: 'and' | 'or'): void {
    if (!this.tree || joiner === this.model.joiner) return;
    this.treeChange.emit(setTopJoiner(this.tree, joiner));
  }

  /** Artifacts hang off the root, so they read from the root's own operator. */
  get artifactJoinerLabel(): string {
    return this.model.rootJoiner === 'and' ? 'and also mentions' : 'or instead';
  }

  get blockerReason(): string {
    return this.model.blockers[0]?.detail ?? '';
  }

  fixInertExclusions(): void {
    if (this.tree) this.treeChange.emit(hoistInertExclusions(this.tree));
  }

  title(group: QbGroup, index: number): string {
    if (this.naming === 'position') {
      // Its own first terms, not its position: a collapsed 42-term bag reading
      // "Group 2" is anonymous, and a number goes stale the moment groups move.
      const named = group.terms
        .map((t) => t.label.trim())
        .filter(Boolean)
        .slice(0, TITLE_TERMS);
      if (!named.length) return `Group ${index + 1}`;
      return named.join(', ') + (group.terms.length > named.length ? '…' : '');
    }
    if (group.context) {
      return this.ordinal('Ambiguous form', group, (g) => !!g.context);
    }
    if (group.guard) {
      return this.ordinal(
        'Form with exception',
        group,
        (g) => !g.context && !!g.guard,
      );
    }
    return index === 0 ? 'Name forms' : 'Alternative forms';
  }

  /**
   * How the terms in a block combine. Takes the shape rather than the type: a
   * group and a zone are unrelated names for the same `{ joiner, terms }` here,
   * and one helper each meant four methods for two behaviours.
   */
  note(block: { joiner: QbJoiner }): string {
    return this.joinerNote(block.joiner);
  }

  /**
   * What this block's terms are, when they all agree and saying so adds
   * something. "Entity block" names the shape a generated company query is built
   * around — typed heads matched both ways, qualified by the tree's context —
   * not merely the presence of an entity term.
   */
  groupKind(group: QbGroup): string {
    const fields = new Set(group.terms.map((t) => t.field));
    if (!fields.size || [...fields].some((f) => isFreeText(f))) return '';
    const typed = fields.has('entity') || fields.has('event_participant');
    if (typed && group.context) return 'Entity block';
    // With one term the caption would only repeat what its own badge says.
    return group.terms.length > 1 ? this.fieldLabel(group.terms[0]) : '';
  }

  /** The block's own terms, never its subs' — each header counts what it shows. */
  termCount(block: { terms: QbTerm[] }): string {
    return plural(block.terms.length, 'term');
  }

  private joinerNote(joiner: QbJoiner): string {
    return joiner === 'all' ? 'all of these' : 'any one of these';
  }

  pillClass(slot: string): string {
    if (slot === 'context') return 'qb-pill--context';
    if (slot === 'guard' || slot === 'excluded') return 'qb-pill--negated';
    if (slot === 'ambiguous') return 'qb-pill--ambiguous';
    return '';
  }

  /**
   * A negated term reads as one wherever it sits. In the structure view a node
   * holds included and excluded terms side by side, and the zones' own
   * inert-exclusion report is easier to believe when the terms it names look
   * different from the ones beside them.
   */
  termClass(term: QbTerm, slot: string): string {
    return term.negated ? 'qb-pill--negated' : this.pillClass(slot);
  }

  /**
   * The add affordance and the field it opens wear the colour of the zone they
   * write into — amber for an ambiguous form, red for what can never match — so
   * the input says which list it is adding to.
   */
  addClass(slot: string): string {
    return this.pillClass(slot).replace('qb-pill--', 'qb-add--');
  }

  isEditable(term: QbTerm): boolean {
    // Every field but free text carries a resolved id the label does not match,
    // so those pills are re-picked rather than renamed.
    return this.editing && isFreeText(term.field) && !term.artifact;
  }

  fieldLabel(term: QbTerm): string {
    if (isFreeText(term.field)) return '';
    if (term.field === 'entity') return entityKind(term.value);
    // A typed head is matched twice, once each way; saying which is the only
    // thing that tells the two pills apart.
    if (term.field === 'event_participant') return 'Participant';
    if (term.field.endsWith('event')) return 'Event';
    if (term.field.startsWith('source')) return 'Source';
    if (term.field === 'query') return 'Saved query';
    return capitalise(term.field);
  }

  volLabel(value: number | null | undefined): string {
    if (value == null) return '';
    if (value >= 1000) {
      const k = value / 1000;
      return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}k`;
    }
    return String(value);
  }

  /** Sizes the input to its content so a pill reads as a pill, not a field. */
  pillWidth(value: string): string {
    return `max(4ch, ${(value?.length ?? 0) + 1}ch)`;
  }

  contextLead(group: QbGroup): string {
    return isIncomplete(group)
      ? 'needs at least one context term'
      : 'only when the article also mentions';
  }

  /**
   * How a nested group joins the one above it, read from the owning node's
   * operator. An OR-ed sub is another way to match; an AND-ed one is a further
   * requirement — saying the wrong one would describe a different query.
   */
  subLead(parent: QbGroup): string {
    // Only the join, never how the sub's own terms combine — the note beside it
    // already says that, and "matching all of / any one of these" read together
    // contradicted itself.
    return parent.subJoiner === 'or' ? 'or, instead' : 'and also';
  }

  isIncomplete(group: QbGroup): boolean {
    return isIncomplete(group);
  }

  // --- Collapsing long term lists ------------------------------------------

  key(path: number[] | null, slot = ''): string {
    return `${pathKey(path)}${slot ? `:${slot}` : ''}`;
  }

  shown(terms: QbTerm[], key: string): QbTerm[] {
    if (this.expanded.has(key) || terms.length <= this.collapseAfter) {
      return terms;
    }
    return terms.slice(0, this.collapseAfter);
  }

  hidden(terms: QbTerm[], key: string): number {
    if (this.expanded.has(key)) return 0;
    return Math.max(0, terms.length - this.collapseAfter);
  }

  expand(key: string): void {
    this.expanded.add(key);
  }

  // --- Pickers --------------------------------------------------------------

  openPicker(key: string): void {
    this.pickerAt = this.pickerAt === key ? null : key;
  }

  closePicker(): void {
    this.pickerAt = null;
  }

  // --- Edits ----------------------------------------------------------------

  commit(path: number[], filter: QueryFilter, negate = false): void {
    if (!this.tree) return;
    const term = negate ? { ...filter, negated: true } : filter;
    this.treeChange.emit(addTerm(this.tree, path, term));
  }

  /** Every structure-view edit addresses one node and leaves the rest alone. */
  onNodeAction(action: QbNodeAction): void {
    if (!this.tree) return;
    if (action.kind === 'operator') {
      this.treeChange.emit(
        setNodeOperator(this.tree, action.path, action.operator),
      );
      return;
    }
    if (action.kind === 'add-group') {
      this.treeChange.emit(addChildGroup(this.tree, action.path));
      return;
    }
    this.treeChange.emit(removeNode(this.tree, action.path));
  }

  /** The picker returns a filter; an artifact goes in the root's own array. */
  commitArtifact(filter: QueryFilter): void {
    const kind = artifactKind(filter.field);
    if (!this.tree || !kind) return;
    this.treeChange.emit(
      addArtifact(this.tree, kind, filter.value, filter.name ?? filter.value),
    );
  }

  dropArtifact(artifact: QbArtifactRef): void {
    if (this.tree) this.treeChange.emit(removeArtifact(this.tree, artifact));
  }

  artifactLabel(artifact: QbArtifactRef): string {
    return artifact.kind === 'query' ? 'Saved query' : 'Portfolio';
  }

  commitExclusion(filter: QueryFilter): void {
    if (this.tree) this.treeChange.emit(addExclusion(this.tree, filter));
  }

  remove(term: QbTerm, group?: QbGroup): void {
    if (!this.tree) return;
    // A grounded block's context and guards only qualify its own term. Taking
    // the last one away leaves a block that matches nothing and blocks a re-run,
    // so the block goes with it.
    const ownTerm =
      !!group?.context && pathKey(term.path) === pathKey(group.path);
    if (ownTerm && group && group.terms.length === 1) {
      this.dropGroup(group);
      return;
    }
    this.treeChange.emit(removeTerm(this.tree, term.path, term.index));
  }

  rename(term: QbTerm, value: string): void {
    const next = (value ?? '').trim();
    if (!this.tree || !next || next === term.label) return;
    this.treeChange.emit(renameTerm(this.tree, term.path, term.index, next));
  }

  dropGroup(group: QbGroup): void {
    if (this.tree) this.treeChange.emit(removeGroup(this.tree, group));
  }

  addRequiredGroup(): void {
    if (this.tree) this.treeChange.emit(addGroup(this.tree));
  }

  addAmbiguousForm(): void {
    if (this.tree) {
      this.treeChange.emit(addGroundedGroup(this.tree, this.model.contentPath));
    }
  }

  /**
   * Whether this group's note is a control. Offered where the host edits topic
   * queries (`allowGroups`): on a company query, "all of these" over name forms
   * would ask for every spelling in one article.
   */
  canSetJoiner(index: number): boolean {
    return this.editing && this.allowGroups && canSetJoiner(this.model, index);
  }

  setGroupJoinerTo(group: QbGroup, joiner: QbJoiner): void {
    if (!this.tree || joiner === group.joiner) return;
    this.treeChange.emit(setGroupJoiner(this.tree, group, joiner));
  }

  canSplit(index: number): boolean {
    return this.editing && canSplit(this.model, index);
  }

  canMerge(index: number, direction: 'up' | 'down'): boolean {
    return this.editing && canMerge(this.model, index, direction);
  }

  /** Why an item is disabled — shown under it, so the control never just vanishes. */
  splitReason(index: number): string {
    return splitUnavailable(this.model, index);
  }

  mergeReason(index: number, direction: 'up' | 'down'): string {
    return mergeUnavailable(this.model, index, direction);
  }

  split(index: number): void {
    const group = this.model.groups[index];
    if (this.tree && group) {
      this.treeChange.emit(splitGroup(this.tree, group.path));
    }
  }

  merge(index: number, direction: 'up' | 'down'): void {
    const group = this.model.groups[index];
    const other = this.model.groups[index + (direction === 'up' ? -1 : 1)];
    if (this.tree && group && other) {
      this.treeChange.emit(mergeGroups(this.tree, group.path, other.path));
    }
  }

  trackByTerm(_: number, term: QbTerm): string {
    return `${pathKey(term.path)}:${term.index}:${term.value}`;
  }

  trackByGroup(_: number, group: QbGroup): string {
    return pathKey(group.path);
  }

  private ordinal(
    label: string,
    group: QbGroup,
    matches: (g: QbGroup) => boolean,
  ): string {
    const siblings = this.model.groups.filter(matches);
    if (siblings.length < 2) return label;
    return `${label} ${siblings.indexOf(group) + 1}`;
  }
}
